// Marble recipe — plan §4.2: bandedNoise (iso-contour bands of a turbulence field) → ramp.
// The canonical procedural material (Perlin 1985). Immutable built-in root (never mutate —
// extend/fork instead).
//
// HOW IT WORKS: Set3 §3.3 — the veins are 2D.  A warped 1D sinusoid (the old `waves` design)
// can only MEANDER its stripes; it cannot break their parallelism, which read as predictable
// NW→SE white/black stripes.  bandedNoise bands the turbulence field ITSELF, so the bands
// form closed loops, swirls and branching veins with NO global direction — the textbook fix.
//
// SCALE CONTRACT:
//   veins   ~0.9 m turbulence cells, banded at ~2.5 cycles/field — one or two large vein
//           systems per view, never a dozen stripes  (colour only)
//   polish  ~4 cm fine fbm, ~0.1 mm relief  (a hair of surface texture; marble is POLISHED —
//           any real relief here destroys the read)
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";

export const MARBLE_RECIPE_ID = "marble";

export const marbleRecipe = new MaterialRecipe({
	id: MARBLE_RECIPE_ID,
	name: "Marble",
	roughness: 0.18, // polished — the lowest roughness in the set
	params: [
		new ParamSchema({key: "marble_scale", label: "Vein cell (m)", kind: "scalar", min: 0.05, max: 20, default: 0.9}),
		// Vein density: band cycles across the field.  Lower = fewer, larger vein systems (the
		// "one or two large vein systems per view" the plan calls for, not a dozen stripes).
		new ParamSchema({key: "marble_veining", label: "Vein density", kind: "scalar", min: 0.2, max: 12, default: 2.5}),
		// sharpness > 1 narrows the dark vein cores (pow on a [0,1] band); 1 = a soft banding.
		new ParamSchema({key: "marble_sharpness", label: "Vein sharpness", kind: "scalar", min: 0.05, max: 8, default: 2.2}),
		// Near-white stone with grey→charcoal veins. t = 0 is the vein core (the band trough),
		// t = 1 the clean stone, so the ramp runs dark → light.
		new ParamSchema({key: "marble_ramp", label: "Marble ramp", kind: "ramp", default: [0, 0.2, 0.2, 0.22, 0.25, 0.5, 0.5, 0.52, 0.55, 0.82, 0.82, 0.83, 1, 0.93, 0.93, 0.92]}),
		new ParamSchema({key: "polish_scale", label: "Polish scale (m)", kind: "scalar", min: 0.005, max: 1, default: 0.04}),
		new ParamSchema({key: "polish_height", label: "Polish relief (m)", kind: "scalar", min: 0, max: 0.005, default: 0.0001}),
	],
	layers: [
		new MaterialLayer({
			id: "veins",
			primitive: "bandedNoise",
			args: {
				scale: {ref: "marble_scale"},
				veining: {ref: "marble_veining"},
				sharpness: {ref: "marble_sharpness"},
				colorRamp: {ref: "marble_ramp"},
				heightScale: 0, // veins are a COLOUR feature; marble's surface is flat
			},
			blend: "over",
			contributes: "color",
		}),
		new MaterialLayer({
			id: "polish",
			primitive: "fbm",
			args: {
				scale: {ref: "polish_scale"},
				octaves: 3,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				heightScale: {ref: "polish_height"},
			},
			blend: "over",
			contributes: "height",
		}),
	],
});
